import { useState, useMemo, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate, useLocation } from "react-router-dom";
import userStore from "../../zustand/userStore";
import {
  GetPaymentPlanDetailsThunk,
  InitiatePaymentThunk,
} from "./redux/thunks";
import { logout } from "../../auth/authSlice";
import Footer from "../../components/Footer";
import SkeletonLoader from "../QuestionPaper/createpaper/components/loader/skeletonLoader";
import "./pricingpage.css";
import { setAppdAiGuest, setAppdAiUser } from "../../utils/appdAiHelpers";

// Original prices for strikethrough display
const originalPrices = {
  "Basic Plan": 15000,
  "Standard Plan": 36000,
  "Pro Plan": 54000,
  "Premium Plan": 72000,
};

// Original prices for individual (customized) plans — per user
const originalCustomPrices = {
  "Any 1 Module + Documents": 2500,
  "Any 2 Modules + Documents": 4500,
  "Any 3 Modules + Documents": 6350,
  "Any 4 Modules + Documents": 8000,
};

// Colors and icons for plans
const planStyles = {
  "Basic Plan": { color: "#10b981", icon: "fa-graduation-cap", iconBg: "#e6f7ef", iconColor: "#10b981" },
  "Standard Plan": { color: "#3b82f6", icon: "fa-building", iconBg: "#e8f0fe", iconColor: "#3b82f6" },
  "Pro Plan": { color: "#8b5cf6", icon: "fa-th-large", iconBg: "#f0ebfe", iconColor: "#8b5cf6" },
  "Premium Plan": { color: "#f59e0b", icon: "fa-certificate", iconBg: "#fef6e6", iconColor: "#f59e0b" },
};

// Module count styles with discount info
const moduleCountStyles = [
  { color: "#4a5568", discount: null }, // 1 Module - no discount
  { color: "#4a5568", discount: "10% off" }, // 2 Modules - 10% discount
  { color: "#4a5568", discount: "15% off" }, // 3 Modules - 15% discount
  { color: "#4a5568", discount: "20% off" }, // 4 Modules - 20% discount
];

// All modules included in Complete Plans
const completeModulesList = [
  { name: "Event Organiser", color: "#4a5568", icon: "fa-calendar-check", iconBg: "#e8f0fe", iconColor: "#3b82f6" },
  { name: "Question Paper", color: "#4a5568", icon: "fa-file-alt", iconBg: "#fee8e8", iconColor: "#ef4444" },
  { name: "Time Table", color: "#4a5568", icon: "fa-clock", iconBg: "#e6f7f7", iconColor: "#0ea5a5" },
  { name: "Academic Calendar", color: "#4a5568", icon: "fa-calendar", iconBg: "#e6f7ef", iconColor: "#10b981" },
  { name: "Teachers Planner", color: "#4a5568", icon: "fa-chalkboard-teacher", iconBg: "#eee8f7", iconColor: "#7c3aed" },
  { name: "Documents", color: "#4a5568", icon: "fa-folder", iconBg: "#fee8e8", iconColor: "#ef4444" },
];

// Available modules for selection
const allModules = [
  { id: 6, name: "Documents", color: "#4a5568" },
  { id: 1, name: "Event Organiser", color: "#4a5568" },
  { id: 2, name: "Question Paper", color: "#4a5568" },
  { id: 3, name: "Time Table", color: "#4a5568" },
  { id: 4, name: "Academic Calendar", color: "#4a5568" },
  {
    id: 5,
    name: "Teachers Planner",
    color: "#4a5568",
    requiresModule: 3,
  },
];

function PricingPage() {
  const { pathname } = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useSelector((store) => store.authSlice);
  const userData = userStore((state) => state.user);

  // Get plan details from Redux (fetched from API)
  const { loader, error, planDetails } = useSelector(
    (store) => store.paymentGateway,
  );

  useEffect(() => {
    if (userData) setAppdAiUser(userData, "pricing");
    else setAppdAiGuest("pricing");
  }, []);
  // Fetch plans from API on mount
  useEffect(() => {
    dispatch(GetPaymentPlanDetailsThunk());
  }, [dispatch]);

  // Separate complete and customized plans from API
  const completePlans =
    planDetails?.filter((p) => p.model_type === "complete") || [];
  const customizedPlans =
    planDetails?.filter((p) => p.model_type === "customized") || [];

  // Complete Plan State
  const [selectedCompletePlan, setSelectedCompletePlan] = useState(null);
  const [completeCouponCode, setCompleteCouponCode] = useState("");
  const [completeAppliedCoupon, setCompleteAppliedCoupon] = useState(null);
  const [completeCouponId, setCompleteCouponId] = useState(null);

  // Customized Plan State
  const [selectedCustomPlan, setSelectedCustomPlan] = useState(null);
  const [selectedModules, setSelectedModules] = useState([]);
  const [numberOfUsers, setNumberOfUsers] = useState(1);
  const [customCouponCode, setCustomCouponCode] = useState("");
  const [customAppliedCoupon, setCustomAppliedCoupon] = useState(null);
  const [customCouponId, setCustomCouponId] = useState(null);

  const [isValidatingCoupon, setIsValidatingCoupon] = useState(false);

  const logOut = () => {
    dispatch(logout());
    navigate("/home");
  };

  // Reset selected modules when custom plan changes - Documents (id: 6) is always included free
  useEffect(() => {
    if (selectedCustomPlan) {
      setSelectedModules([6]); // Pre-select Documents (free bonus)
    } else {
      setSelectedModules([]);
    }
  }, [selectedCustomPlan]);
  // Get module count from selected custom plan
  const selectedModuleCount = selectedCustomPlan?.plan_type || 0;

  // Calculate customized plan price
  const customizedPrice = useMemo(() => {
    if (!selectedCustomPlan) return 0;
    return Number(selectedCustomPlan.price) * numberOfUsers;
  }, [selectedCustomPlan, numberOfUsers]);

  // Calculate final price with discount for complete plan
  const completePlanFinalPrice = useMemo(() => {
    if (!selectedCompletePlan) return 0;
    const basePrice = Number(selectedCompletePlan.price);
    if (!completeAppliedCoupon) return basePrice;
    const { discount } = completeAppliedCoupon;
    if (typeof discount === "string" && discount.includes("%")) {
      const percentage = parseFloat(discount.replace("%", ""));
      return Math.round(basePrice - (basePrice * percentage) / 100);
    }
    return Math.max(0, basePrice - parseFloat(discount));
  }, [selectedCompletePlan, completeAppliedCoupon]);

  // Calculate final price with discount for customized plan
  const customizedFinalPrice = useMemo(() => {
    if (!customAppliedCoupon) return customizedPrice;
    const { discount } = customAppliedCoupon;
    if (typeof discount === "string" && discount.includes("%")) {
      const percentage = parseFloat(discount.replace("%", ""));
      return Math.round(customizedPrice - (customizedPrice * percentage) / 100);
    }
    return Math.max(0, customizedPrice - parseFloat(discount));
  }, [customizedPrice, customAppliedCoupon]);

  // Check if customized plan configuration is valid
  const isCustomizedValid = useMemo(() => {
    const selectedWithoutDocuments = selectedModules.filter(
      (id) => id !== 6,
    ).length;
    return (
      selectedCustomPlan &&
      selectedWithoutDocuments === selectedModuleCount &&
      numberOfUsers >= 1
    );
  }, [selectedCustomPlan, selectedModules, selectedModuleCount, numberOfUsers]);

  // Handle module selection for customized plan
  const handleModuleToggle = (moduleId) => {
    if (!selectedCustomPlan) return;
    if (moduleId === 6) return;

    if (selectedModules.includes(moduleId)) {
      let newSelection = selectedModules.filter((id) => id !== moduleId);
      // If removing Time Table, also remove Teachers Planner
      if (moduleId === 3 && selectedModules.includes(5)) {
        newSelection = newSelection.filter((id) => id !== 5);
      }
      setSelectedModules(newSelection);
    } else {
      // Don't count Documents (id: 6) towards the limit
      const selectedWithoutDocuments = selectedModules.filter((id) => id !== 6);
      if (selectedWithoutDocuments.length >= selectedModuleCount) {
        alert(
          `You can only select ${selectedModuleCount} module${selectedModuleCount > 1 ? "s" : ""}.`,
        );
        return;
      }

      let newSelection = [...selectedModules, moduleId];

      // If adding Teachers Planner, ensure Time Table is selected
      if (moduleId === 5 && !selectedModules.includes(3)) {
        if (selectedModules.length >= selectedModuleCount - 1) {
          alert(
            "Teachers Planner requires Time Table. Please make room for it.",
          );
          return;
        }
        newSelection = [...newSelection, 3];
      }
      setSelectedModules(newSelection);
    }
  };

  // Coupon handlers for Complete Plan
  const handleCompleteApplyCoupon = async () => {
    if (!completeCouponCode.trim())
      return alert("Please enter a coupon code.");
    if (!selectedCompletePlan) return alert("Please select a plan first.");
    setIsValidatingCoupon(true);
    try {
      const res = await fetch(
        "https://web1024.docmaster.in:5010/api/coupons/validate",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "x-tenant-db": "main_admin_db",
          },
          body: JSON.stringify({
            code: completeCouponCode,
            plan_types: [String(selectedCompletePlan.model_id)],
          }),
        },
      );
      const data = await res.json();
      if (!data.success) return alert(data.message || "Invalid coupon");
      setCompleteCouponId(data.id);
      setCompleteAppliedCoupon({
        code: completeCouponCode,
        discount: data.discount || 0,
      });
    } catch (error) {
      alert("Error validating coupon");
    } finally {
      setIsValidatingCoupon(false);
    }
  };

  const handleCompleteRemoveCoupon = () => {
    setCompleteCouponCode("");
    setCompleteAppliedCoupon(null);
    setCompleteCouponId(null);
  };

  // Coupon handlers for Customized Plan
  const handleCustomApplyCoupon = async () => {
    if (!customCouponCode.trim())
      return alert("Please enter a coupon code.");
    if (!selectedCustomPlan) return alert("Please select a plan first.");
    setIsValidatingCoupon(true);
    try {
      const res = await fetch(
        "https://web1024.docmaster.in:5010/api/coupons/validate",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "x-tenant-db": "main_admin_db",
          },
          body: JSON.stringify({
            code: customCouponCode,
            plan_types: [String(selectedCustomPlan.model_id)],
          }),
        },
      );
      const data = await res.json();
      if (!data.success) return alert(data.message || "Invalid coupon");
      setCustomCouponId(data.id);
      setCustomAppliedCoupon({
        code: customCouponCode,
        discount: data.discount || 0,
      });
    } catch (error) {
      alert("Error validating coupon");
    } finally {
      setIsValidatingCoupon(false);
    }
  };

  const handleCustomRemoveCoupon = () => {
    setCustomCouponCode("");
    setCustomAppliedCoupon(null);
    setCustomCouponId(null);
  };

  // Create coupon link
  const createCouponLink = async (couponId) => {
    if (!couponId) return null;
    const schoolId = userData?.admin_id;
    try {
      const response = await fetch(
        "https://web1024.docmaster.in:5010/api/coupons/createCouponLink",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "x-tenant-db": "main_admin_db",
          },
          body: JSON.stringify({
            schoolId,
            couponId,
            createdById: schoolId,
            status: "incompleted",
          }),
        },
      );
      if (response.ok) {
        const data = await response.json();
        return data.insertId;
      }
      return null;
    } catch (error) {
      console.error("Error creating coupon link:", error);
      return null;
    }
  };

  // Payment handler for Complete Plan
  const handleCompletePayment = async () => {
    if (!user) return alert("Please login to continue.");
    if (!selectedCompletePlan) return alert("Please select a plan.");

    const linkData = await createCouponLink(completeCouponId);

    // const body = {
    //   txnRefNo: Date.now(),
    //   amount: completePlanFinalPrice,
    //   txnType: "Pay",
    //   orderInfo: String(selectedCompletePlan.model_id),
    //   email: user.email,
    //   firstName: "sample",
    //   lastName: "sample",
    //   street: "Delhi",
    //   city: "",
    //   state: "",
    //   zip: 506002,
    //   phone: user?.contact_number || 0,
    //   UDF01: user.admin_id,
    //   UDF02: user?.id,
    //   UDF03: [],
    //   UDF04: "complete",
    //   UDF05: 1,
    //   UDF06: linkData || "",
    // };

    const body = {
      txnRefNo: Date.now(),
      amount: completePlanFinalPrice,
      // amount: 1,
      txnType: "Pay",
      orderInfo: String(selectedCompletePlan.model_id),
      email: user.email,
      firstName: "sample",
      lastName: "sample",
      street: "Delhi",
      city: "",
      state: "",
      zip: 506002,
      phone: user?.contact_number || 0,
      udf21: user.admin_id,
      udf22: user?.id,
      udf23: [],
      udf24: "complete",
      udf25: 1,
      udf26: linkData || "",
      udf27: String(selectedCompletePlan.model_id),
    };
    console.log("Complete Plan Payment Body:", body);
    dispatch(InitiatePaymentThunk(body));
  };

  // Payment handler for Customized Plan
  const handleCustomizedPayment = async () => {
    if (!user) return alert("Please login to continue.");
    if (!isCustomizedValid) return alert("Please complete your selection.");

    // Check if Teachers Planner is selected without Time Table
    if (selectedModules.includes(5) && !selectedModules.includes(3)) {
      return alert("Please select Time Table to use Teacher's Planner.");
    }

    const linkData = await createCouponLink(customCouponId);

    // const body = {
    //   txnRefNo: Date.now(),
    //   amount: customizedFinalPrice,
    //   txnType: "Pay",
    //   orderInfo: String(selectedCustomPlan.model_id),
    //   email: user.email,
    //   firstName: "sample",
    //   lastName: "sample",
    //   street: "Delhi",
    //   city: "",
    //   state: "",
    //   zip: 506002,
    //   phone: user?.contact_number || 0,
    //   UDF01: user.admin_id,
    //   UDF02: user?.id,
    //   UDF03: selectedModules.join(","),
    //   UDF04: "customized",
    //   UDF05: numberOfUsers,
    //   UDF06: linkData || "",
    // };

    const body = {
      txnRefNo: Date.now(),
      amount: customizedFinalPrice,
      //amount: 1,
      txnType: "Pay",
      orderInfo: String(selectedCustomPlan.model_id),
      email: user.email,
      firstName: "sample",
      lastName: "sample",
      street: "Delhi",
      city: "",
      state: "",
      zip: 506002,
      phone: user?.contact_number || 0,
      udf21: user.admin_id,
      udf22: user?.id,
      udf23: selectedModules.join(","),
      udf24: "customized",
      udf25: numberOfUsers,
      udf26: linkData || "",
      udf27: String(selectedCustomPlan.model_id),
    };
    console.log("Customized Plan Payment Body:", body);
    dispatch(InitiatePaymentThunk(body));
  };

  // Show loader while fetching plans
  if (loader) return <SkeletonLoader type={1} />;
  if (error) {
    alert(error);
    return null;
  }

  return (
    <>
      {/* Navbar */}
      <nav
        className="navbar navbar-expand-lg p-0"
        style={{
          background: "linear-gradient(135deg, #0b3a83, #1e5799, #3a8fd9)",
          fontSize: "0.85rem",
          boxShadow: "0 2px 12px rgba(11, 58, 131, 0.3)",
        }}
      >
        <div
          className="container-fluid px-0 mx-auto"
          style={{ maxWidth: "100%" }}
        >
          {/* Home icon - visible on mobile instead of hamburger */}
          <Link to="/dashboard" className="nav-link text-white d-lg-none ms-3" style={{ fontSize: "1.2rem" }}>
            <i className="fa fa-home"></i>
          </Link>
          <div className="collapse navbar-collapse" id="navbarNav">
            <ul className="navbar-nav ps-4 w-100 d-flex gap-3 justify-content-start">
              <li
                className={`nav-item ${["/dashboard"].includes(pathname) ? "active" : ""}`}
              >
                <Link to="/dashboard" className="nav-link text-white">
                  <i
                    className="fa fa-home"
                    style={{ pointerEvents: "none" }}
                  ></i>{" "}
                  Home
                </Link>
              </li>
            </ul>
          </div>
          <div className="d-flex align-items-center justify-content-end gap-3">
            <div className="dropdown me-3">
              <button
                className="btn btn-light dropdown-toggle d-flex align-items-center"
                type="button"
                id="userDropdown"
                data-bs-toggle="dropdown"
                aria-expanded="false"
                style={{ borderRadius: "25px", fontWeight: "600" }}
              >
                <i className="fa fa-user me-2"></i>
                {userData.name}
              </button>
              <ul
                className="dropdown-menu dropdown-menu-end"
                aria-labelledby="userDropdown"
              >
                <li>
                  <span className="dropdown-item">Profile</span>
                </li>
                <li>
                  <span className="dropdown-item" onClick={logOut}>
                    Log Out
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className="pricing-main-content">
        <div className="pricing-container">
          {/* Header */}
          <div className="pricing-header">
            <h1>
              Choose Your Plan
            </h1>
            <p>
              All plans include 12 months of access.
            </p>
          </div>

          {/* Two Columns Layout */}
          <div className="pricing-columns">
            {/* Column 2: Customized Plan for Individual Users */}
            <div className="pricing-column individual-column">
              <div className="column-header">
                <div className="header-content-wrapper">
                  <div className="header-text-section">
                    <h2>Customized Plan for Individuals</h2>
                    <p className="column-subtitle">Build your own bundle</p>
                  </div>
                  <div className="header-price-section individual">
                    <div className="header-price-label">Starts From</div>
                    <div className="header-price-values">
                      <span className="header-price-original">₹2,500/user</span>
                      <span className="header-price-current individual">
                        ₹250/user
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Checkboxes Row */}
              <div className="checkbox-row custom-checkbox-row">
                {customizedPlans.map((plan, index) => {
                  const style = moduleCountStyles[index] || {
                    color: "#667eea",
                    discount: null,
                  };
                  return (
                    <label
                      key={plan.model_id}
                      className={`checkbox-item custom-checkbox-item ${selectedCustomPlan?.model_id === plan.model_id ? "selected" : ""}`}
                      style={{ "--item-color": style.color }}
                    >
                      <input
                        type="checkbox"
                        checked={selectedCustomPlan?.model_id === plan.model_id}
                        // onChange={() => setSelectedCustomPlan(selectedCustomPlan?.model_id === plan.model_id ? null : plan)}
                        onChange={() => {
                          if (selectedCustomPlan?.model_id === plan.model_id) {
                            setSelectedCustomPlan(null);
                          } else {
                            setSelectedCustomPlan(plan);
                            setSelectedCompletePlan(null);
                            setCompleteCouponCode("");
                            setCompleteAppliedCoupon(null);
                            setCompleteCouponId(null);
                          }
                        }}
                      />
                      <span className="checkbox-custom"></span>
                      <span className="checkbox-label">{plan.model_name}</span>
                      <div className="checkbox-price-row">
                        {originalCustomPrices[plan.model_name] && (
                          <span className="checkbox-original-price">
                            ₹{originalCustomPrices[plan.model_name].toLocaleString()}
                          </span>
                        )}
                        <span className="checkbox-price">
                          ₹{plan.price}/user
                        </span>
                        {style.discount && (
                          <span className="checkbox-discount">
                            {style.discount}
                          </span>
                        )}
                      </div>
                    </label>
                  );
                })}
              </div>

              {/* Big Details Card */}
              <div
                className={`details-card ${selectedCustomPlan ? "active" : ""}`}
                style={{
                  "--card-color": selectedCustomPlan
                    ? moduleCountStyles[
                      customizedPlans.indexOf(selectedCustomPlan)
                    ]?.color || "#ec4899"
                    : "#ec4899",
                }}
              >
                {selectedCustomPlan ? (
                  <>
                    <div className="card-header-row">
                      <div className="plan-info">
                        <div>
                          <div className="card-title">
                            Select {selectedModuleCount} Module
                            {selectedModuleCount > 1 ? "s" : ""}
                          </div>
                          <div className="card-subtitle-fun">
                            <span className="progress-dots">
                              {[...Array(selectedModuleCount)].map((_, i) => (
                                <span
                                  key={i}
                                  className={`dot ${i < selectedModules.filter((id) => id !== 6).length ? "filled" : ""}`}
                                ></span>
                              ))}
                            </span>
                            {selectedModules.filter((id) => id !== 6).length}/
                            {selectedModuleCount} selected{" "}
                          </div>
                        </div>
                      </div>
                      <div className="price-per-user">
                        {originalCustomPrices[selectedCustomPlan.model_name] > 0 && (
                          <span className="original-price" style={{ display: "block", textAlign: "right" }}>
                            ₹{originalCustomPrices[selectedCustomPlan.model_name].toLocaleString()}
                          </span>
                        )}
                        <div style={{ display: "flex", alignItems: "baseline", gap: "4px", justifyContent: "flex-end" }}>
                          <span className="offer-label">Offer Price</span>
                          <span className="price-value">
                            ₹{selectedCustomPlan.price}
                          </span>
                          <span className="price-label">/user</span>
                        </div>
                      </div>
                    </div>

                    {/* Module Selection Grid */}
                    <div className="modules-selection-grid">
                      {allModules.map((module) => {
                        const isSelected = selectedModules.includes(module.id);
                        const selectedWithoutDocuments = selectedModules.filter(
                          (id) => id !== 6,
                        ).length;
                        const isDisabled =
                          module.id === 6
                            ? false
                            : !isSelected &&
                            selectedWithoutDocuments >= selectedModuleCount;

                        return (
                          <div
                            key={module.id}
                            className={`module-select-card ${isSelected ? "selected" : ""} ${isDisabled ? "disabled" : ""} ${module.id === 6 ? "locked" : ""}`}
                            onClick={() => handleModuleToggle(module.id)}
                            style={{ "--module-color": module.color }}
                          >
                            <div className="module-checkbox-indicator">
                              {isSelected ? "✓" : ""}
                            </div>
                            <span className="module-name">{module.name}</span>
                            {module.requiresModule && (
                              <span className="module-note">
                                Requires Time Table
                              </span>
                            )}
                            {module.id === 6 && (
                              <span className="module-note">
                                Free with plan
                              </span>
                            )}
                          </div>
                        );
                      })}
                    </div>

                    {/* Number of Users */}
                    <div className="users-section">
                      <label>Number of Users:</label>
                      <div className="users-input">
                        <button
                          onClick={() =>
                            setNumberOfUsers(Math.max(1, numberOfUsers - 1))
                          }
                          disabled={customAppliedCoupon !== null}
                        >
                          -
                        </button>
                        <input
                          type="number"
                          value={numberOfUsers}
                          onChange={(e) =>
                            setNumberOfUsers(
                              Math.max(1, parseInt(e.target.value) || 1),
                            )
                          }
                          min="1"
                          disabled={customAppliedCoupon !== null}
                        />
                        <button
                          onClick={() => setNumberOfUsers(numberOfUsers + 1)}
                          disabled={customAppliedCoupon !== null}
                        >
                          +
                        </button>
                      </div>
                    </div>
                    {/* Price Breakdown */}
                    <div className="price-breakdown">
                      <div className="breakdown-row">
                        <span>Price per user</span>
                        <span>₹{selectedCustomPlan.price}</span>
                      </div>
                      <div className="breakdown-row">
                        <span>Number of users</span>
                        <span>× {numberOfUsers}</span>
                      </div>
                      <div className="breakdown-row subtotal">
                        <span>Subtotal</span>
                        <span>₹{customizedPrice.toLocaleString()}</span>
                      </div>
                    </div>

                    {/* Coupon & Payment */}
                    <div className="card-footer">
                      <div className="coupon-row">
                        <input
                          type="text"
                          placeholder="Enter coupon code"
                          value={customCouponCode}
                          onChange={(e) => setCustomCouponCode(e.target.value)}
                          disabled={customAppliedCoupon !== null}
                        />
                        {customAppliedCoupon ? (
                          <button
                            className="coupon-btn remove"
                            onClick={handleCustomRemoveCoupon}
                          >
                            Remove
                          </button>
                        ) : (
                          <button
                            className="coupon-btn apply"
                            onClick={handleCustomApplyCoupon}
                            disabled={isValidatingCoupon}
                          >
                            {isValidatingCoupon ? "..." : "Apply"}
                          </button>
                        )}
                      </div>
                      {customAppliedCoupon && (
                        <div className="coupon-success">
                          Coupon "{customAppliedCoupon.code}" applied (
                          {typeof customAppliedCoupon.discount === "string"
                            ? customAppliedCoupon.discount
                            : `₹${customAppliedCoupon.discount}`}{" "}
                          off)
                        </div>
                      )}

                      <div className="total-row">
                        <span>Total Amount:</span>
                        <span className="total-price">
                          ₹{customizedFinalPrice.toLocaleString()}
                        </span>
                      </div>

                      <button
                        className={`pay-button individual ${!isCustomizedValid ? "disabled" : ""}`}
                        onClick={handleCustomizedPayment}
                        disabled={!isCustomizedValid}
                      >
                        {isCustomizedValid
                          ? `Pay Now ₹${customizedFinalPrice.toLocaleString()}`
                          : selectedModules.length < selectedModuleCount
                            ? `Select ${selectedModuleCount - selectedModules.filter((id) => id !== 6).length} more`
                            : "Complete selection"}
                      </button>
                    </div>
                  </>
                ) : (
                  <div className="empty-state">
                    <span className="empty-text">
                      Select the number of modules above to get started
                    </span>
                  </div>
                )}
              </div>
            </div>
            {/* Column 1: Complete Modules for School */}
            <div className="pricing-column school-column">
              <div className="column-header">
                <div className="header-content-wrapper">
                  <div className="header-text-section">
                    <h2>Complete Modules for School</h2>
                    <p className="column-subtitle">
                      Everything your school needs
                    </p>
                  </div>
                  <div className="header-price-section">
                    <div className="header-price-label">Starts From</div>
                    <div className="header-price-values">
                      <span className="header-price-original">₹15,000</span>
                      <span className="header-price-current">₹5,000</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Checkboxes Row */}
              <div className="checkbox-row">
                {completePlans.map((plan) => {
                  const style = planStyles[plan.model_name] || {
                    color: "#4a5568",
                  };
                  return (
                    <label
                      key={plan.model_id}
                      className={`checkbox-item ${selectedCompletePlan?.model_id === plan.model_id ? "selected" : ""}`}
                      style={{ "--item-color": style.color }}
                    >
                      <input
                        type="checkbox"
                        checked={
                          selectedCompletePlan?.model_id === plan.model_id
                        }
                        // onChange={() => setSelectedCompletePlan(selectedCompletePlan?.model_id === plan.model_id ? null : plan)}
                        onChange={() => {
                          if (
                            selectedCompletePlan?.model_id === plan.model_id
                          ) {
                            setSelectedCompletePlan(null);
                          } else {
                            setSelectedCompletePlan(plan);
                            setSelectedCustomPlan(null);
                            setCustomCouponCode("");
                            setCustomAppliedCoupon(null);
                            setCustomCouponId(null);
                          }
                        }}
                      />
                      <span className="checkbox-custom"></span>
                      <div className="checkbox-text-wrapper">
                        <div className="plan-icon-wrapper" style={{ background: style.iconBg }}>
                          <i className={`fa ${style.icon}`} style={{ color: style.iconColor }}></i>
                        </div>
                        <span className="checkbox-label">
                          {plan.model_name.replace(" Plan", "")}
                        </span>
                        <span className="checkbox-users">
                          {plan.total_users} users
                        </span>
                      </div>
                    </label>
                  );
                })}
              </div>

              {/* Big Details Card */}
              <div
                className={`details-card ${selectedCompletePlan ? "active" : ""}`}
                style={{
                  "--card-color": selectedCompletePlan
                    ? planStyles[selectedCompletePlan.model_name]?.color ||
                    "#667eea"
                    : "#667eea",
                }}
              >
                {selectedCompletePlan ? (
                  <>
                    <div className="card-header-row">
                      <div className="plan-info">
                        <div>
                          <div className="card-title">
                            {selectedCompletePlan.model_name}
                          </div>
                          <div className="card-users">
                            Up to {selectedCompletePlan.total_users} users
                          </div>
                        </div>
                      </div>
                      <div className="card-pricing">
                        {originalPrices[selectedCompletePlan.model_name] > 0 && (
                          <span className="original-price">
                            ₹{originalPrices[selectedCompletePlan.model_name].toLocaleString()}
                          </span>
                        )}
                        <div className="price-row">
                          <span className="offer-label">Offer Price</span>
                          <span className="current-price">
                            ₹{completePlanFinalPrice.toLocaleString()}
                          </span>
                          <span className="per-year">/year</span>
                        </div>
                      </div>
                    </div>

                    <div className="card-features-row">
                      <div
                        className="feature-badge"
                        style={{
                          background: "linear-gradient(135deg, #0b3a83, #1e5799)",
                        }}
                      >
                        Unlimited Usage
                      </div>
                      <div
                        className="feature-badge"
                        style={{
                          background: "linear-gradient(135deg, #d97706, #e8860c)",
                        }}
                      >
                        {selectedCompletePlan.validity_months} Months
                      </div>
                    </div>

                    <div className="card-modules-section">
                      <span className="modules-title">
                        All Modules Included:
                      </span>
                      <div className="modules-grid">
                        {completeModulesList.map((module, idx) => (
                          <div
                            key={idx}
                            className="module-item"
                            style={{ "--module-color": module.color }}
                          >
                            <div className="module-icon-box" style={{ background: module.iconBg }}>
                              <i className={`fa ${module.icon}`} style={{ color: module.iconColor }}></i>
                            </div>
                            <span>{module.name}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Coupon & Payment */}
                    <div className="card-footer">
                      <div className="coupon-row">
                        <input
                          type="text"
                          placeholder="Enter coupon code"
                          value={completeCouponCode}
                          onChange={(e) =>
                            setCompleteCouponCode(e.target.value)
                          }
                          disabled={completeAppliedCoupon !== null}
                        />
                        {completeAppliedCoupon ? (
                          <button
                            className="coupon-btn remove"
                            onClick={handleCompleteRemoveCoupon}
                          >
                            Remove
                          </button>
                        ) : (
                          <button
                            className="coupon-btn apply"
                            onClick={handleCompleteApplyCoupon}
                            disabled={isValidatingCoupon}
                          >
                            {isValidatingCoupon ? "..." : "Apply"}
                          </button>
                        )}
                      </div>
                      {completeAppliedCoupon && (
                        <div className="coupon-success">
                          Coupon "{completeAppliedCoupon.code}" applied (
                          {typeof completeAppliedCoupon.discount === "string"
                            ? completeAppliedCoupon.discount
                            : `₹${completeAppliedCoupon.discount}`}{" "}
                          off)
                        </div>
                      )}

                      <div className="total-row">
                        <span>Total Amount:</span>
                        <span className="total-price">
                          ₹{completePlanFinalPrice.toLocaleString()}
                        </span>
                      </div>

                      <button
                        className="pay-button school"
                        onClick={handleCompletePayment}
                      >
                        Pay Now ₹{completePlanFinalPrice.toLocaleString()}
                      </button>
                    </div>
                  </>
                ) : (
                  <div className="empty-state">
                    <span className="empty-text">
                      Select a plan above to view details
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
}

export default PricingPage;